import { createSelector } from 'reselect';
import createCachedSelector from 're-reselect';
import { difference, uniq, keys, values, flatten, memoize } from 'lodash';
import {
  buildsByBuildTypes,
  buildHealthPageReducer,
  dispatchersByJobIds,
  dispatchersReducer,
  groupScenariosByRunner,
  jobsReducer,
  jobIdsByBuildId,
  paramsByJobIds,
  buildsById
} from '../reducers';
import {
  getRCBuildsByProducts,
  getRCBuildsSinceLastReleaseByProducts
} from './build';
import {
  annotation as annotationHelpers,
  build as buildHelpers,
  buildHealth as buildHealthHelpers,
  colours as colourHelpers,
  dispatcherTypes as dispatcherTypesHelper,
  jobNames as jobNameHelpers,
  utils
} from '../helpers';
import * as annotationSelectors from './annotation';

const cachedBuildsByBuildType = createCachedSelector(
  buildHealthPageReducer.getBuildHealthProduct,
  buildsByBuildTypes,
  (_state, type) => type,
  (product, buildsByType, type) => {
    const builds = buildsByType[type] || [];
    const productNames = buildHelpers.getProdNamesByProductName(product);
    const filteredBuilds = builds.filter(
      b => productNames.includes(b.product));
    return filteredBuilds;
  }
)(
  (_state, type) => type
);

export const getBuildsByProducts = createSelector(
  buildHealthPageReducer.getBuildHealthInfo,
  getRCBuildsByProducts,
  getRCBuildsSinceLastReleaseByProducts,
  (buildHealthPageInfo, rcBuildsByProducts, rcBuildsSinceLastReleaseByProducts) => {
    const buildsByProducts = buildHealthPageInfo.extended ?
      rcBuildsByProducts : rcBuildsSinceLastReleaseByProducts;
    return buildsByProducts;
  }
);

export const getBuildHealthPageBuilds = createSelector(
  (state) => cachedBuildsByBuildType(state, buildHelpers.BUILD_RC),
  (state) => cachedBuildsByBuildType(state, buildHelpers.LATEST_RELEASE),
  (rcBuilds, releaseBuilds) => {
    if (releaseBuilds.length === 0)
    {
      return [];
    }
    const latestRelease = releaseBuilds[0];
    const rcBuildsAfterLatestRelease =
      rcBuilds.filter(rcBuild => rcBuild.build > latestRelease.build);
    const buildHealthPageBuilds =
      [...releaseBuilds, ...rcBuildsAfterLatestRelease];

    return buildHealthPageBuilds;
  }
);

export const getScenariosStatusSummaryByBuildIds = createSelector(
  getBuildHealthPageBuilds,
  jobIdsByBuildId,
  groupScenariosByRunner,
  (builds, jobIdsByBuildId, scenariosByRunner) => {
    const overallSummaryByBuildId = builds.reduce(
      (resultByBuildIds, build) =>
      {
        const jobIds = jobIdsByBuildId[build.id] ? jobIdsByBuildId[build.id] : [];
        const runnerJobScenarios =
         uniq(flatten(jobIds.map(id => scenariosByRunner[id]).filter(s => s)));
        const scenariosStatusSummary =
         utils.propertyAccumulatorReducer(runnerJobScenarios, 'status');
        resultByBuildIds[build.id] = scenariosStatusSummary;
        return resultByBuildIds;
      }, {}
    );
    return overallSummaryByBuildId;
  }
);

export const getBuildHealthPageDispatchersByBuildIds = createSelector(
  buildHealthPageReducer.getBuildHealthDispatcherTriggerBy,
  getBuildHealthPageBuilds,
  jobIdsByBuildId,
  dispatchersByJobIds,
  paramsByJobIds,
  jobsReducer.jobsById,
  (dispatcherTriggerBy, builds, jobIdsByBuildId,
    dispatchersByJobIds, paramsByJobIds, jobsById) =>
  {
    const dispatchersByBuildIds = builds.reduce((result, build) => {
      const jobIds = jobIdsByBuildId[build.id] ? jobIdsByBuildId[build.id] : [];
      let filteredDispatchers =
       flatten(jobIds.map(id => dispatchersByJobIds[id])).filter(d => d);
      filteredDispatchers = filteredDispatchers.filter(
        dispatcher => dispatcher && paramsByJobIds[dispatcher.job_id] &&
        paramsByJobIds[dispatcher.job_id]['JENKINS_ENV:TRIGGERED_BY'] &&
        dispatcherTriggerBy &&
        dispatcherTriggerBy.some(triggerBy =>
          paramsByJobIds[dispatcher.job_id]['JENKINS_ENV:TRIGGERED_BY'].
            toLowerCase().includes(triggerBy.toLowerCase())));
      // adding the extra param name because of the ticket STS-2320
      // case metrics and engine metrics share the same dispatchertype
      // need job name to seperate them
      filteredDispatchers.forEach(d => {
        const job = jobsById[d.job_id];
        const params = paramsByJobIds[d.job_id];
        d.name = job ? job.name : '';
        d.number = job ? job.number : 0;
        d.triggeredBy = params ? params['JENKINS_ENV:TRIGGERED_BY'] : '';
      });
      result[build.id] = filteredDispatchers;
      return result;
    }, {});
    return dispatchersByBuildIds;
  }
);

export const getUIInstallerLatestDispatcherIdsByBuildIds = createSelector(
  getBuildHealthPageDispatchersByBuildIds,
  getBuildHealthPageBuilds,
  (dispatchersByBuildIds, builds) => {
    const buildIds = builds.map(b => b.id);
    const installerLatestDispatcherIdsByBuildIds =
    buildIds.reduce((result, buildId) => {
      const dispatchers = dispatchersByBuildIds[buildId] || [];
      const UIInstallerDispatchers = dispatchers.filter(
        d => d.dispatcher_type === dispatcherTypesHelper.INSTALLERTESTS);
      const UIInstallerDispatchersByTriggeredBy = utils.
        propertyAccumulatorReducer(UIInstallerDispatchers, 'triggeredBy');
      const latestDispatcherIds =
      keys(UIInstallerDispatchersByTriggeredBy).reduce(
        (latestDispatcherIds, triggeredBy) => {
          const groupedDispatchers = UIInstallerDispatchersByTriggeredBy[triggeredBy];
          const latestDisaptcher = groupedDispatchers.sort((a, b) => b.id - a.id)[0];
          latestDispatcherIds.push(latestDisaptcher.id);
          return latestDispatcherIds;
        }, []);
      result[buildId] = latestDispatcherIds;
      return result;
    }, {});
    return installerLatestDispatcherIdsByBuildIds;
  });

export const getUIInstallerLatestDispatcherIds = createSelector(
  getUIInstallerLatestDispatcherIdsByBuildIds,
  (installerLatestDispIdsByBuildIds) => memoize((buildIds) => {
    const result = buildIds.map(id => installerLatestDispIdsByBuildIds[id] &&
      installerLatestDispIdsByBuildIds[id]);
    return flatten(result);
  })
);

export const getBuildIdByDispatcherIdPairs = createSelector(
  buildsById,
  getBuildHealthPageDispatchersByBuildIds,
  (buildsById, dispatchersByBuildIds) => {
    if (keys(buildsById).length === 0)
    {
      return {};
    }
    const pairs = keys(dispatchersByBuildIds).reduce((result, buildId) => {
      const buildIdsByDispatcherIds = {};
      const dispatchers = dispatchersByBuildIds[buildId] || [];
      dispatchers.forEach(d => buildIdsByDispatcherIds[d.id] = buildId);
      result = { ...result, ...buildIdsByDispatcherIds };
      return result;
    }, {});
    return pairs;
  }
);

// concat of ENGINEMETRICS can be removed with STS-2320
export const getAllDispatcherTypes = createSelector(
  getBuildHealthPageDispatchersByBuildIds,
  (dispatchersByBuildId) => [
    ...utils.propertyReduceToSet(
      flatten(values(dispatchersByBuildId)),
      'dispatcher_type'
    ).values(),
    dispatcherTypesHelper.ENGINEMETRICS
  ].sort()
);

export const getDispatchersResultsByDispatcherIdsAndBuildIds = createSelector(
  getBuildHealthPageDispatchersByBuildIds,
  (dispatchersByBuildIds) => {
    const dispatchersResultsByBuildIds =
    keys(dispatchersByBuildIds).reduce((resultByBuildIds, buildId) => {
      const dispatchers = dispatchersByBuildIds[buildId];
      const dispatchersResultsByBuildIds =
       dispatchers.reduce((resultByDispatcherIds, dispatcher) => {
         resultByDispatcherIds[dispatcher.id] = dispatcher.scenarios_summary || {};
         return resultByDispatcherIds;
       }, {});
      resultByBuildIds[buildId] = dispatchersResultsByBuildIds;
      return resultByBuildIds;
    }, {});
    return dispatchersResultsByBuildIds;
  }
);

export const getBuildHealthScoresByDispatcherIdByBuildId = createSelector(
  getBuildHealthPageBuilds,
  getDispatchersResultsByDispatcherIdsAndBuildIds,
  (state) => annotationSelectors.getAnnotationsByExtraInfoByTypedId(
    state,
    annotationHelpers.EXTRA_INFO_PROPS.DISPATCHER_ID,
    annotationHelpers.BUILD_TYPED
  ),
  (builds, resultsByDispatcherByBuild, annotationsByDispIdByBuild) => {
    //Dispatcher could run on two builds which belong to same build number.(e.g UITests)
    //When adding annotation to a dispatcher, it is only linked to one of the builds
    //if that happens, we will only analyze the dispatcher with the build
    //which is linked to that annotation.
    const selectedProductBuildIds = new Set(builds.map(b => b.id));
    const buildIdsWithAnnotations = keys(annotationsByDispIdByBuild).
      filter(id => selectedProductBuildIds.has(parseInt(id)));
    const overallDispIdsWithAnnotations = new Set(flatten(buildIdsWithAnnotations.
      map(id => keys(annotationsByDispIdByBuild[id]))));

    const scoreByBuildId = builds.reduce((resultByBuild, build) => {
      const scenarioResultsByDispatcherId = resultsByDispatcherByBuild[build.id] || {};
      const annotationsByDispatcherId = annotationsByDispIdByBuild[build.id] || {};
      const dispatcherIds = keys(scenarioResultsByDispatcherId);

      const annotationsDispIdsOfGivenBuild = new Set(keys(annotationsByDispatcherId));
      const dispatcherIdsToAnalyze = dispatcherIds.filter(id =>
        !overallDispIdsWithAnnotations.has(id) ||
         annotationsDispIdsOfGivenBuild.has(id));

      resultByBuild[build.id] = dispatcherIdsToAnalyze
        .reduce((resultByDispatcher, dispatcherId) => {
          let dispatcherResult = buildHealthHelpers.upVoteScoreData(false);
          const scenarioResults = scenarioResultsByDispatcherId[dispatcherId];
          if (keys(scenarioResults).length === 0) {
            return resultByDispatcher;
          }
          const annotations = annotationsByDispatcherId[dispatcherId] || [];

          // if any manual down vote, then override with downVote result
          if (annotations.some(a => a.status === annotationHelpers.DOWN_VOTE)) {
            dispatcherResult = buildHealthHelpers.downVoteScoreData(true);
          }

          else if (annotations.some(a => a.status === annotationHelpers.UP_VOTE)) {
            dispatcherResult = buildHealthHelpers.upVoteScoreData(true);
          }

          // automatic downVote
          else if (scenarioResults['ERROR'] || scenarioResults['FAILED']) {
            dispatcherResult = buildHealthHelpers.downVoteScoreData(false);
          }

          // some still running, don't finalize a score yet
          else if (scenarioResults['RUNNING']) {
            dispatcherResult = buildHealthHelpers.neutralVoteScoreData(false);
          }
          resultByDispatcher[dispatcherId] = Object.assign({}, dispatcherResult);
          return resultByDispatcher;
        }, {});

      return resultByBuild;
    }, {});

    return scoreByBuildId;
  }
);

// Remove dispatcher type determination from this selector when STS-2320 is fixed,
// the types can then already be returned from getBuildHealthScoresByDispatcherIdByBuildId
export const mapBuildSortedScoresByDispatcherType = createSelector(
  getBuildHealthPageBuilds,
  getAllDispatcherTypes,
  getBuildHealthScoresByDispatcherIdByBuildId,
  (state) => dispatchersReducer.dispatchersById(state),
  (state) => jobsReducer.jobsById(state),
  getUIInstallerLatestDispatcherIdsByBuildIds,
  (builds, dispatcherTypes, scoresByDispIdByBuildId, dispatchersById, jobsById,
    latestInstallerDispIdsbyBuildIds ) => {
    const buildNumbers = uniq(builds.map(b => b.build)).sort();
    const buildsByBuildNumber = utils.propertyAccumulatorReducer(builds, 'build');
    // this and data value injection can be added to the result objects in
    // getBuildHealthScoresByDispatcherIdByBuildId when STS-2320 is fixed
    const dispatcherTypeOrdersByType = dispatcherTypes
      .reduce((result, type, index) => {
        result[type] = index;
        return result;
      }, {});

    const scoresByDispatcherIdsByBuildNumbers = [];
    const installerLatestDispIdsByBuildNumbers = [];
    buildNumbers.forEach(bn => {
      const buildIds = buildsByBuildNumber[bn].map(b => b.id);
      let scoresByDispatcherIdsByBuildNumber = {};
      let installerLatestDispIdsByBuildNumber = [];
      buildIds.forEach(id => {
        const scoresByDisptcherIds = scoresByDispIdByBuildId[id] || {};
        const installerLatestDispIds = latestInstallerDispIdsbyBuildIds[id] || [];
        scoresByDispatcherIdsByBuildNumber =
         { ...scoresByDispatcherIdsByBuildNumber, ...scoresByDisptcherIds };
        installerLatestDispIdsByBuildNumber =
        [ ...installerLatestDispIdsByBuildNumber, ...installerLatestDispIds ];
      });
      scoresByDispatcherIdsByBuildNumbers.push(scoresByDispatcherIdsByBuildNumber);
      installerLatestDispIdsByBuildNumbers.push(installerLatestDispIdsByBuildNumber);
    });

    let index = -1;
    // this can be replaced with utils.accumulatorReducerByFunc call post STS-2320
    const mappedResult = scoresByDispatcherIdsByBuildNumbers
      .reduce((result, scoresByDispatcher) => {
        index ++;
        // used to keep track of which types have been entered
        const enteredDispatcherTypes = new Set();
        let installerDispsOverallResult = {};
        const latestDispIdInInstallerGroup =
         Math.max(...installerLatestDispIdsByBuildNumbers[index]);

        const dispatcherIds = keys(scoresByDispatcher);
        for (const id of dispatcherIds) {
          const dispatcher = dispatchersById[id];
          if (!dispatcher) {
            continue;
          }

          let dispatcherType = dispatcher.dispatcher_type;
          if (dispatcherType === dispatcherTypesHelper.CASEMETRICS) {
            const job = jobsById[dispatcher.job_id];
            if (job && job.name === jobNameHelpers.ENGINE_METRICS_DISPATCHER) {
              dispatcherType = dispatcherTypesHelper.ENGINEMETRICS;
            }
          }

          // only take last dispatcher for any given type, so if already entered
          // then pop and enter later one
          if (dispatcherType !== dispatcherTypesHelper.INSTALLERTESTS)
          {
            if (enteredDispatcherTypes.has(dispatcherType)) {
              result[dispatcherType].pop();
            }
            result[dispatcherType] = [
              ...(result[dispatcherType] || []),
              {
                ...scoresByDispatcher[id],
                data: dispatcherTypeOrdersByType[dispatcherType]
              }
            ];
          }
          else
          {
            const checkDispAnnotation =
             latestDispIdInInstallerGroup === parseInt(id);
            if (!installerLatestDispIdsByBuildNumbers[index] ||
              !installerLatestDispIdsByBuildNumbers[index].includes(parseInt(id)))
            {
              continue;
            }
            const currentDispResult = scoresByDispatcher[id];
            if (enteredDispatcherTypes.has(dispatcherType)) {
              switch (installerDispsOverallResult.score)
              {
                case buildHealthHelpers.downVoteScore:
                  if (checkDispAnnotation && currentDispResult.isManual)
                  {
                    installerDispsOverallResult = currentDispResult;
                  }
                  break;
                case buildHealthHelpers.upVoteScore:
                  installerDispsOverallResult = currentDispResult;
                  break;
                case buildHealthHelpers.neutralVoteScore:
                  if (currentDispResult.score ===
                    buildHealthHelpers.downVoteScoreData.score ||
                    (checkDispAnnotation && currentDispResult.isManual))
                  {
                    installerDispsOverallResult = currentDispResult;
                  }
              }
              result[dispatcherType].pop();
            }
            else {
              installerDispsOverallResult = currentDispResult;
            }
            result[dispatcherType] = [
              ...(result[dispatcherType] || []),
              {
                ...installerDispsOverallResult,
                data: dispatcherTypeOrdersByType[dispatcherType]
              }
            ];
          }

          // add type to entered set
          enteredDispatcherTypes.add(dispatcherType);
        }

        // enter undefined for any missing dispatcher types
        for (const type of difference(dispatcherTypes, [...enteredDispatcherTypes])) {
          result[type] = [...(result[type] || []), undefined];
        }
        return result;
      }, {});
    return mappedResult;
  }
);

export const getDispatchersResultsByDispatcherIdsByBuildId = createSelector(
  getDispatchersResultsByDispatcherIdsAndBuildIds,
  (resultsByDispatcherId) => memoize((buildId) => {
    return resultsByDispatcherId[buildId];
  })
);

export const getBuildHealthSelectorInitialValues = createSelector(
  buildHealthPageReducer.getBuildHealthInfo,
  getBuildHealthPageBuilds,
  (buildHealthPageInfo, builds) => {
    const product = buildHealthPageInfo.product;

    if (!buildHelpers.PRODUCTS_BY_PROD[product] ||
       buildHealthPageInfo.buildNumber !== 0 || builds.length === 0) {
      return {};
    }

    const buildNumbers = builds.length > 0 ?
      uniq(builds.map(b => b.build).sort((a, b) => b - a)) : [];
    const latestBuildNumber = buildNumbers[0] || 0;
    const filteredBuilds = builds && builds.filter(b => b.build === latestBuildNumber);
    const latestBuildIds = filteredBuilds ? filteredBuilds.map(build => build.id) : [0];
    buildHealthPageInfo.buildNumber = latestBuildNumber;
    buildHealthPageInfo.buildIds = latestBuildIds;

    return buildHealthPageInfo;
  }
);

export const getTrendingChartPropMappedData = createSelector(
  mapBuildSortedScoresByDispatcherType,
  (scoresByDispatcherType) => {
    const result = {};
    const dispatcherTypes = keys(scoresByDispatcherType).sort();
    const props = ['data', 'pointOptions'];
    props.forEach(prop => {
      result[prop] = [];
      result[prop] = dispatcherTypes.map(type =>
        scoresByDispatcherType[type].map(s => s && s[prop])
      );
    });

    dispatcherTypes.forEach(type => {
      const newSeries = {
        label: type,
        backgroundColor: colourHelpers.colours.blue,
        borderColor: colourHelpers.colours.blue,
        pointStyle: 'rect',
        showLine: false
      };
      result.series = [...(result.series || []), newSeries];
    });
    return result;
  }
);

export const getAnnotationsByDispatcherIds = createSelector(
  buildHealthPageReducer.getBuildHealthInfo,
  (state) => annotationSelectors.getAnnotationsByExtraInfoByTypedId(
    state,
    annotationHelpers.EXTRA_INFO_PROPS.DISPATCHER_ID,
    annotationHelpers.BUILD_TYPED
  ),
  (buildHealthPageInfo, annotationsByDispIdByBuildId) => {
    const annotationsByDispatcherIds = buildHealthPageInfo.buildIds.
      reduce((result, buildId) => {
        const annotations = annotationsByDispIdByBuildId[buildId];
        if (annotations)
        {
          result = { ...result, ...annotations };
        }
        return result;
      }, {});
    return annotationsByDispatcherIds;
  }
);

export const getPanelStylesByDispatcherIds = createSelector(
  buildHealthPageReducer.getBuildHealthInfo,
  getBuildHealthScoresByDispatcherIdByBuildId,
  (buildHealthPageInfo, scoresByDispatcherIdByBuildId) => {
    const panelStyleByDispatcherIds = buildHealthPageInfo.buildIds.
      reduce((result, buildId) => {
        const panelStyles = scoresByDispatcherIdByBuildId[buildId];
        if (panelStyles)
        {
          result = { ...result, ...panelStyles };
        }
        return result;
      }, {});
    return panelStyleByDispatcherIds;
  }
);

export const getPanelStylesByDispatcherType = createSelector(
  buildHealthPageReducer.getBuildHealthBuildNumber,
  getBuildHealthPageBuilds,
  mapBuildSortedScoresByDispatcherType,
  (buildNumber, builds, scoresByDispatcherType) => {
    if (builds.length === 0)
    {
      return {};
    }
    const buildNumbers = uniq(builds.map(b => b.build)).sort();
    const selectedBuildNumber = buildNumber;
    const buildNumberIndex = buildNumbers.findIndex(n => n === selectedBuildNumber);
    const panelStylesByDispatcherType = keys(scoresByDispatcherType).
      reduce((result, type) => {
        const scoresArray = scoresByDispatcherType[type];
        const panelStyles = scoresArray[buildNumberIndex];
        result[type] = panelStyles;
        return result;
      }, {});

    return panelStylesByDispatcherType;
  }
);


export const getResultsByDispatcherIds = createSelector(
  buildHealthPageReducer.getBuildHealthInfo,
  getDispatchersResultsByDispatcherIdsByBuildId,
  (buildHealthPageInfo, resultsByDispatcherIdsByBuildId) => {
    const resultsByDispatcherIds =
    buildHealthPageInfo.buildIds.reduce((result, buildId) => {
      const dispatchersResults = resultsByDispatcherIdsByBuildId(buildId);
      if (dispatchersResults)
      {
        result = { ...result, ...dispatchersResults };
      }
      return result;
    }, {});
    return resultsByDispatcherIds;
  }
);


// WEBPACK FOOTER //
// src/selectors/buildHealth.js